import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Botpoison from '@botpoison/browser';
import {
  Home,
  CheckCircle,
  Star,
  Phone,
  Mail,
  MapPin,
  Clock,
  DollarSign,
  Shield,
  Users,
  Award,
  Heart,
  ChevronDown,
  Menu,
  X,
  Instagram,
  Facebook,
  Linkedin,
  Building
} from 'lucide-react';
import logo from './assets/logo.jpeg';
import johnPhoto from './assets/john.webp';
import topAgentBadge from './assets/top-agent-verified-member.webp';
import hidevLogo from './assets/hidev_logo.png';
import heroImage from './assets/hero.jpeg';
import HomePage from './HomePage';
import About from './About';
import './App.css';

// Initialize Botpoison
const botpoison = new Botpoison({
  publicKey: "pk_b8a10263-d7d9-4631-ac3a-943f0fe306ac"
});

// Component to scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Navigation component that works with both pages
const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const handleNavLinkClick = () => {
    setIsMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      // If not on home page, navigate to home first
      window.location.href = `/#${sectionId}`;
    } else {
      // If on home page, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="nav">
      <div className="container">
        <div className="nav-content">
          <Link to="/" className="nav-brand">
            <img src={logo} alt="Logo" className="brand-logo-img" />
          </Link>

          <div className={`nav-links ${isMenuOpen ? 'nav-links-open' : ''}`}>
            <Link to="/" className="nav-link" onClick={handleNavLinkClick}>Home</Link>
            <button
              className="nav-link"
              onClick={() => scrollToSection('process')}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Our Process
            </button>
            <button
              className="nav-link"
              onClick={() => scrollToSection('faq')}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              FAQ
            </button>
            <button
              className="nav-link"
              onClick={() => scrollToSection('testimonials')}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Testimonials
            </button>
            <Link to="/about" className="nav-link" onClick={handleNavLinkClick}>About</Link>
            <button
              className="btn btn-primary"
              onClick={() => scrollToSection('contact')}
            >
              Unlock Your Offer
            </button>
          </div>

          <button
            className="nav-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </nav>
  );
};

const App: React.FC = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Parallax effect with throttling and requestAnimationFrame
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrolled = window.pageYOffset;

          // Parallax effect - hero background moves slower in same direction
          const heroBackground = document.querySelector('.hero-background-image') as HTMLElement;
          if (heroBackground) {
            // Move background in same direction but slower for proper parallax
            const parallaxSpeed = scrolled * 0.3;
            heroBackground.style.transform = `translate3d(0, ${parallaxSpeed}px, 0)`;
          }

          // Scroll to top button
          setShowScrollTop(scrolled > 500);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Router>
      <div className="App">
        <Navigation />

        <ScrollToTop />

        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                showScrollTop={showScrollTop}
                scrollToTop={scrollToTop}
              />
            }
          />
          <Route path="/about" element={<About />} />
        </Routes>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              {/* Brand Section */}
              <div className="footer-brand">
                <div className="footer-logo">
                  <img src={logo} alt="Logo" className="footer-logo-img" />
                </div>
                <p className="text-small text-gray mt-m">
                  Your trusted Bay Area cash home buyer.
                </p>
              </div>

              {/* All Links in Single Row for Desktop, 2 Columns for Mobile */}
              <div className="footer-links">
                <Link to="/" className="footer-link">Home</Link>
                <a href="/#process" className="footer-link">Our Process</a>
                <Link to="/about" className="footer-link">About</Link>
                <a href="/#faq" className="footer-link">FAQ</a>
                <a href="/#contact" className="footer-link">Unlock Cash Offer</a>
                <a href="/#testimonials" className="footer-link">Reviews</a>
                <a href="/#process" className="footer-link">How It Works</a>
                <a href="tel:+16504851233" className="footer-link">Call Now</a>
              </div>

              {/* Hi Dev Section */}
              <div className="footer-hidev">
                <p className="text-small text-gray mb-m">Website by</p>
                <a href="https://hidevmobile.com" target="_blank" rel="noopener noreferrer" className="hidev-link">
                  <img src={hidevLogo} alt="Hi Dev Mobile" className="hidev-logo" />
                </a>
              </div>
            </div>

            {/* Social Links Below */}
            {/* <div className="footer-bottom">
              <div className="footer-social">
                <a href="https://www.instagram.com/johnschrupre/" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                  <Instagram size={20} />
                </a>
                <a href="https://www.facebook.com/john.schrup.1" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                  <Facebook size={20} />
                </a>
                <a href="https://www.linkedin.com/in/john-schrup-598b02153/" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                  <Linkedin size={20} />
                </a>
                <a href="https://www.zillow.com/profile/jcschrup" target="_blank" rel="noopener noreferrer" className="footer-social-link">
                  <Building size={20} />
                </a>
              </div>
            </div> */}
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;
